import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import 'moment/locale/pt-br';
import { Box, Grid } from '@mui/material';

import { api } from '~/services/api';

import Header from '~/components/Header';
import OrderListPanel from '~/components/OrderListPanel';
import DateRangePicker from '~/components/DateRangePicker';
import ChallengePagePanel from '~/components/SalesPersonPanelPiloto/ChallengePagePanel';

import Snackbar from '~/util/SnackBar';
import { FARM } from '~/util/brands';

import { handleOrdersResponse } from '~/util/handleOrdersResponse';
import { signOut } from '~/store/modules/auth/actions';
import { formatDateToApi } from '~/util/format';
import * as Style from './styles';
import LoadingComponent from './LoadingComponent';
import { useLocation } from 'react-router-dom';
import SalesTerms from '~/components/SalesTerms';
import CustomHeader from '~/components/CustomHeader';

function AdoroCodigoChallenge() {
    const [orders, setOrders] = useState([]);
    const [ordersLoaded, setOrdersLoaded] = useState(false);
    const [challengeData, setChallengeData] = useState({});
    const [
        aggregatedOrdersIndicators,
        setAggregatedOrdersIndicators,
    ] = useState({ value: 0 });
    const [panelLoaded, setPanelLoaded] = useState(true);
    const [salesPercentage, setSalesPercentage] = useState('0');
    const [currentQuotaValue, setCurrentQuotaValue] = useState(0);
    const [quotaLabel, setQuotaLabel] = useState('Cota');
    const [userProfile, setUserProfile] = useState(null);

    const dispatch = useDispatch();
    const location = useLocation();

    const salesCodeFromParams = new URLSearchParams(location.search).get('salesCode');

    const userProfileFromStore = useSelector(state => state.user.profile);
    const { initialDate, finalDate, isFaturado } = useSelector(
        state => state.status
    );

    useEffect(() => {
        if (salesCodeFromParams) {
            const fetchSalesPersonProfile = async () => {
                try {
                    const response = await api.post(`session/user-data`, {
                        salesCode: salesCodeFromParams,
                    });
                    setUserProfile(response.data.user); 
                } catch (err) {
                    console.error("Erro ao buscar dados da vendedora", err);
                    setUserProfile(userProfileFromStore);
                }
            };
            fetchSalesPersonProfile();
        } else {
            setUserProfile(userProfileFromStore);
        }
    }, [salesCodeFromParams, userProfileFromStore]);

    useEffect(() => {
        if (!initialDate || !finalDate) return;

        const activeSalesCodes = salesCodeFromParams 
            ? [salesCodeFromParams] 
            : userProfileFromStore.users_sales_code.map(s => s.codigo_vendedor);

        const activeSalesCodeSingle = salesCodeFromParams 
            ? salesCodeFromParams 
            : userProfileFromStore.users_sales_code[0]?.codigo_vendedor;
            setPanelLoaded(false);

        const getAggregatedCapturedOrders = async () => {
            try {
                const params = {
                    fatured: 'false',
                    startDate: formatDateToApi(initialDate),
                    endDate: formatDateToApi(finalDate),
                    salesCode: activeSalesCodes,
                    transaction: 'true',
                    brands: [FARM],
                };

                const response = await api.get(
                    '/salesperson/aggregated-orders',
                    { params }
                );
                setAggregatedOrdersIndicators(
                    response.data.data.aggregatedByBrand[FARM]
                );
            } catch (err) {
                Snackbar.error(
                    'Erro retornando os indicadores de pedidos capturados'
                );
            }
        };

        const getChallengeData = async () => {
            try {
                const response = await api.get(
                    '/salesperson/adoro-codigo-challenge',
                    {
                        params: {
                            startDate: formatDateToApi(initialDate),
                            endDate: formatDateToApi(finalDate),
                            salesCode: activeSalesCodeSingle,
                        },
                    }
                );

                setChallengeData(response.data);
            } catch (err) {
                Snackbar.error('Erro retornando dados do desafio');
            }
        };

        const getOrders = async () => {
            setOrdersLoaded(false);
            try {
                const params = {
                    startDate: formatDateToApi(initialDate),
                    endDate: formatDateToApi(finalDate),
                    salesCode: activeSalesCodes,
                    orderType: isFaturado ? 'invoiced' : 'captured',
                    brands: [2],
                    transaction: 'true',
                };

                const response = await api.get('/salesperson/orders', {
                    params,
                });
                const formattedOrders = await handleOrdersResponse(
                    response.data.data,
                    false
                );
                setOrders(formattedOrders);
            } catch (err) {
                Snackbar.error('Erro retornando lista de pedidos');
            }
            setOrdersLoaded(true);
        };

        const canMakeRequests =
            initialDate &&
            finalDate &&
            userProfileFromStore?.users_sales_code?.[0]?.codigo_vendedor;

        if (canMakeRequests) {
            Promise.all([
                getAggregatedCapturedOrders(),
                getChallengeData(),
                getOrders(),
            ])
                .catch(error => {
                    if (error.response && error.response.status === 401) {
                        Snackbar.error('Token expirado. Faça login novamente');
                        dispatch(signOut());
                    }
                })
                .finally(() => setPanelLoaded(true));
        } else {
            setPanelLoaded(true);
        }
    }, [
        initialDate,
        finalDate,
        isFaturado,
        userProfileFromStore,
        salesCodeFromParams,
        dispatch,
    ]);

    useEffect(() => {
        const vendas = aggregatedOrdersIndicators?.value;
        const { total = 0, quota = 0, super_quota = 0 } = challengeData;

        if (!vendas || !total) return;

        const metaBasica = total * quota;
        const metaSuper = total * super_quota;

        const isSuper = vendas >= metaBasica;
        const metaAtual = isSuper ? metaSuper : metaBasica;

        setQuotaLabel(isSuper ? 'Supercota' : 'Cota');
        setCurrentQuotaValue(metaAtual);
        setSalesPercentage(((vendas / metaAtual) * 100).toFixed(2));
    }, [challengeData, aggregatedOrdersIndicators]);

    return (
        <>
         <SalesTerms/>
            {location.search.includes('salesCode') ? (
                <CustomHeader
                    title={
                        userProfile &&
                        `${userProfile?.nome
                            .split(' ')[0]
                            .toLowerCase()} (${salesCodeFromParams})`
                    }
                />
            ) : (
                <Header />
            )}
            <Style.DatePickerContainer>
                <Box style={{ width: '100%' }}>
                    <DateRangePicker hidden={!panelLoaded} assessment />
                </Box>
            </Style.DatePickerContainer>
            {!panelLoaded && <LoadingComponent />}
            <Grid container justifyContent="space-evenly">
                <Grid item xs={12}>
                    <ChallengePagePanel
                        panelLoaded={panelLoaded}
                        challengeData={challengeData}
                        totalSalesValue={aggregatedOrdersIndicators.value}
                        salesPercentage={salesPercentage}
                        quotaLabel={quotaLabel}
                        currentQuotaValue={currentQuotaValue}
                    />
                </Grid>
                <Style.LateralPanelContainer item xs={12}>
                    <OrderListPanel
                        ordersLoaded={ordersLoaded}
                        orders={orders}
                    />
                </Style.LateralPanelContainer>
            </Grid>
        </>
    );
}

export default AdoroCodigoChallenge;
